// @ts-nocheck
import React, { useState, useEffect } from 'react'
import { Modal, Input, Select, Table, Button, Space, Tag, InputNumber, message, Empty } from 'antd'
import { useQuery } from '@tanstack/react-query'
import MaterialIcon from './MaterialIcon'
import { gmailService } from '../services/gmailService'

interface PriceBookItem {
  id: string
  name: string
  description?: string
  sku?: string
  itemType: string
  category?: string
  subcategory?: string
  unitPrice: number
  unit?: string
  isTaxable: boolean
  images?: string[]
}

interface SelectedItem extends PriceBookItem {
  quantity: number
  subtotal: number
}

interface PriceBookSelectorProps {
  visible: boolean
  onClose: () => void
  onAddItems?: (items: SelectedItem[]) => void
  onSelect?: (items: SelectedItem[]) => void // Alternative prop name for estimates
  buttonText?: string // Custom button text
}

const PriceBookSelector: React.FC<PriceBookSelectorProps> = ({
  visible,
  onClose,
  onAddItems,
  onSelect,
  buttonText,
}) => {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<string | undefined>()
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])

  const { data, isLoading } = useQuery({
    queryKey: ['priceBookItems', search, category],
    queryFn: () => gmailService.getPriceBookItems({ search, category }),
    enabled: visible,
    staleTime: 30000,
  })

  const items = data?.data?.items || []
  const categories = data?.data?.categories || []

  const handleQuantityChange = (itemId: string, quantity: number) => {
    setSelectedItems(prev => 
      prev.map(item => 
        item.id === itemId 
          ? { ...item, quantity, subtotal: quantity * item.unitPrice }
          : item
      )
    )
  }

  const handleAddToSelection = (item: PriceBookItem) => {
    const existing = selectedItems.find(i => i.id === item.id)
    if (existing) {
      message.info('Item already added!')
      return
    }

    const newItem: SelectedItem = {
      ...item,
      quantity: 1,
      subtotal: item.unitPrice,
    }

    setSelectedItems(prev => [...prev, newItem])
    setSelectedRowKeys(prev => [...prev, item.id])
    message.success(`Added ${item.name}`)
  }

  const handleRemoveItem = (itemId: string) => {
    setSelectedItems(prev => prev.filter(item => item.id !== itemId))
    setSelectedRowKeys(prev => prev.filter(key => key !== itemId))
  }

  const handleAddItems = () => {
    if (selectedItems.length === 0) {
      message.warning('Please select at least one item')
      return
    }

    // Call either onSelect or onAddItems (for backward compatibility)
    const callback = onSelect || onAddItems
    if (callback) {
      callback(selectedItems)
    }
    
    setSelectedItems([])
    setSelectedRowKeys([])
    onClose()
  }

  const columns = [
    {
      title: 'Item',
      key: 'item',
      render: (record: PriceBookItem) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.name}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.description}
            </div>
          )}
          {record.sku && (
            <Tag style={{ marginTop: '4px', fontSize: '11px' }}>SKU: {record.sku}</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => category && <Tag color="blue">{category}</Tag>,
    },
    {
      title: 'Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 100,
      render: (price: number, record: PriceBookItem) => (
        <div>
          <div style={{ fontWeight: 500 }}>${price.toFixed(2)}</div>
          {record.unit && (
            <div style={{ fontSize: '11px', color: '#666' }}>per {record.unit}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (record: PriceBookItem) => {
        const isSelected = selectedRowKeys.includes(record.id)
        return (
          <Button
            size="small"
            type={isSelected ? 'default' : 'primary'}
            icon={<MaterialIcon icon={isSelected ? 'check' : 'add'} style={{ fontSize: '16px' }} />}
            onClick={() => isSelected ? handleRemoveItem(record.id) : handleAddToSelection(record)}
          >
            {isSelected ? 'Added' : 'Add'}
          </Button>
        )
      },
    },
  ]

  const selectedColumns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 100,
      render: (price: number) => `$${price.toFixed(2)}`,
    },
    {
      title: 'Qty',
      key: 'quantity',
      width: 100,
      render: (record: SelectedItem) => (
        <InputNumber
          min={1}
          max={999}
          value={record.quantity}
          onChange={(value) => handleQuantityChange(record.id, value || 1)}
          size="small"
          style={{ width: '70px' }}
        />
      ),
    },
    {
      title: 'Subtotal',
      key: 'subtotal',
      width: 100,
      render: (record: SelectedItem) => (
        <span style={{ fontWeight: 500 }}>${record.subtotal.toFixed(2)}</span>
      ),
    },
    {
      title: '',
      key: 'remove',
      width: 50,
      render: (record: SelectedItem) => (
        <Button
          size="small"
          danger
          type="text"
          icon={<MaterialIcon icon="delete" style={{ fontSize: '16px' }} />}
          onClick={() => handleRemoveItem(record.id)}
        />
      ),
    },
  ]

  const total = selectedItems.reduce((sum, item) => sum + item.subtotal, 0)

  return (
    <Modal
      title={
        <Space>
          <MaterialIcon icon="shopping_cart" style={{ fontSize: '24px' }} />
          Add Price Book Items
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="add"
          type="primary"
          onClick={handleAddItems}
          disabled={selectedItems.length === 0}
          icon={<MaterialIcon icon={buttonText ? "add_shopping_cart" : "email"} style={{ fontSize: '18px' }} />}
        >
          {buttonText || `Add to Email`} ({selectedItems.length} items - ${total.toFixed(2)})
        </Button>,
      ]}
      styles={{
        body: { maxHeight: '70vh', overflowY: 'auto' }
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Search and Filter */}
        <Space style={{ width: '100%' }}>
          <Input
            placeholder="Search items..."
            prefix={<MaterialIcon icon="search" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder="All Categories"
            value={category}
            onChange={setCategory}
            style={{ width: 200 }}
            allowClear
          >
            {categories.map((cat: string) => (
              <Select.Option key={cat} value={cat}>
                {cat}
              </Select.Option>
            ))}
          </Select>
        </Space>

        {/* Selected Items Section */}
        {selectedItems.length > 0 && (
          <div style={{ 
            background: '#f0f7ff', 
            padding: '16px', 
            borderRadius: '8px',
            border: '1px solid #91d5ff'
          }}>
            <div style={{ fontWeight: 500, marginBottom: '12px', fontSize: '14px' }}>
              <MaterialIcon icon="playlist_add_check" style={{ fontSize: '18px', marginRight: '8px' }} />
              Selected Items ({selectedItems.length})
            </div>
            <Table
              dataSource={selectedItems}
              columns={selectedColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
            <div style={{ 
              marginTop: '12px', 
              textAlign: 'right', 
              fontSize: '16px', 
              fontWeight: 600 
            }}>
              Total: ${total.toFixed(2)}
            </div>
          </div>
        )}

        {/* Available Items */}
        <div>
          <div style={{ fontWeight: 500, marginBottom: '12px', fontSize: '14px' }}>
            <MaterialIcon icon="inventory_2" style={{ fontSize: '18px', marginRight: '8px' }} />
            Available Items
          </div>
          <Table
            dataSource={items}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            pagination={{ pageSize: 10 }}
            size="small"
            locale={{
              emptyText: (
                <Empty
                  description="No items found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
            }}
          />
        </div>
      </Space>
    </Modal>
  )
}

export default PriceBookSelector

